// Typographie — Space Grotesk (display) + Inter (corps), portée de
// mobile/src/theme/fonts.ts. En web on peut utiliser `font-weight` (pas de
// faux-gras navigateur) : chaque variante porte donc son poids explicite.
// Les familles pointent vers les variables next/font (--font-display / --font-body).

export const fontStacks = {
  display:
    "var(--font-display), 'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  body: "var(--font-body), 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  // Fraunces — la serif éditoriale déjà chargée par `layout.tsx`. Elle était utilisée en
  // familles et tailles écrites à la main dans les séparateurs d'année ; elle a désormais
  // ses variantes ici, comme les deux autres.
  serif:
    "var(--font-serif), 'Fraunces', ui-serif, Georgia, 'Times New Roman', serif",
} as const;

export type FontFamily = keyof typeof fontStacks;

export interface FontVariant {
  family: FontFamily;
  size: number; // px
  lineHeight: number; // px
  weight: number;
  letterSpacing?: number; // px
}

export const typography = {
  displayXl: { family: 'display', size: 32, lineHeight: 36, weight: 700, letterSpacing: -0.5 },
  title: { family: 'display', size: 24, lineHeight: 30, weight: 600, letterSpacing: -0.5 },
  subtitle: { family: 'display', size: 20, lineHeight: 26, weight: 600 },
  // Serif éditoriale : les séparateurs d'année de la bibliothèque, et le titre d'une carte
  // depuis qu'il a quitté la photographie pour le bandeau blanc. Le titre n'est plus tenu
  // aux 24 px que réclamait le contraste sur une image — la taille redevient un choix.
  serifSection: { family: 'serif', size: 22, lineHeight: 28, weight: 600, letterSpacing: -0.4 },
  serifTitle: { family: 'serif', size: 21, lineHeight: 26, weight: 600, letterSpacing: -0.3 },
  bodyLg: { family: 'body', size: 17, lineHeight: 24, weight: 400 },
  body: { family: 'body', size: 15, lineHeight: 22, weight: 400 },
  bodyMedium: { family: 'body', size: 15, lineHeight: 22, weight: 500 },
  bodySm: { family: 'body', size: 13, lineHeight: 18, weight: 400 },
  bodySmMedium: { family: 'body', size: 13, lineHeight: 18, weight: 500 },
  bodySmSemibold: { family: 'body', size: 13, lineHeight: 18, weight: 600 },
  caption: { family: 'body', size: 11, lineHeight: 14, weight: 500 },
  button: { family: 'body', size: 15, lineHeight: 20, weight: 600 },
} satisfies Record<string, FontVariant>;

export type TypographyToken = keyof typeof typography;

// Convertit une variante en style CSS applicable (React CSSProperties).
export function fontStyle(token: TypographyToken): {
  fontFamily: string;
  fontSize: string;
  lineHeight: string;
  fontWeight: number;
  letterSpacing?: string;
} {
  const v: FontVariant = typography[token];
  return {
    fontFamily: fontStacks[v.family],
    fontSize: `${v.size}px`,
    lineHeight: `${v.lineHeight}px`,
    fontWeight: v.weight,
    ...(v.letterSpacing !== undefined ? { letterSpacing: `${v.letterSpacing}px` } : {}),
  };
}
